// src/pages/EditStone.jsx
import React, { useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { motion } from "framer-motion";
import Select from "react-select";
import { FaArrowLeft } from "react-icons/fa";
import { Link, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";

import {
  handleDecimalKeyDown,
  handleDecimalPaste,
  handleIntegerBeforeInput,
  handleIntegerKeyDown,
  handleIntegerPaste,
} from "../config/fieldsHelper";

/* RTK Query */
import {
  useGetStoneByIdQuery,
  useUpdateStoneMutation,
} from "../toolkitServices/stoneApi";
import { useGetAllGroupedStoneAttributesQuery } from "../toolkitServices/stoneAttributeApi";

/* Local slice update */
import { setStoneList } from "../slices/stoneSlice";

/* ---------- UI tokens: compact + border-only validation ---------- */
const baseInput =
  "w-full px-3 py-2.5 border rounded-lg text-[14px] focus:outline-none";
const okRing =
  "border-gray-300 focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]";
const errRing =
  "border-red-400 focus:ring-4 focus:ring-red-200 focus:border-red-500";

const makeSelectStyles = (hasError) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    height: 40,
    borderWidth: 1,
    borderColor: hasError ? "#ef4444" : state.isFocused ? "#9481ce" : "#e5e7eb",
    boxShadow: "none",
    ":hover": { borderColor: hasError ? "#ef4444" : "#9481ce" },
    borderRadius: 10,
    fontSize: "0.92rem",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 10px" }),
  indicatorsContainer: (base) => ({ ...base, height: 40 }),
  menu: (base) => ({ ...base, zIndex: 50, borderRadius: 10, overflow: "hidden" }),
  option: (base, state) => ({
    ...base,
    padding: "8px 12px",
    backgroundColor: state.isSelected ? "#efeafd" : state.isFocused ? "#f7f6ff" : "white",
    color: "#111827",
  }),
  placeholder: (base) => ({ ...base, color: "#9ca3af" }),
  singleValue: (base) => ({ ...base, color: "#111827" }),
});

const UNIT_OPTS = [
  { value: "gram", label: "Gram" },
  { value: "carat", label: "Carat" },
];
const DEDUCT_OPTS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

const EditStone = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // current stone
  const { data: stoneRes, isLoading: loadingStone, isError: stoneErr } = useGetStoneByIdQuery(id);

  // update mutation
  const [updateStone, { isLoading: saving }] = useUpdateStoneMutation();

  // existing list (for optimistic/local patch)
  const stonesData = useSelector((state) => state.stones?.stones || []);

  // attributes for dropdowns
  const { data: groupedAttrs, isLoading: attrLoading, isError: attrError } =
    useGetAllGroupedStoneAttributesQuery();

  // options
  const shapeOptions = useMemo(
    () => (groupedAttrs?.data?.shape || []).map((a) => ({ value: a._id, label: a.name })),
    [groupedAttrs]
  );
  const colorOptions = useMemo(
    () => (groupedAttrs?.data?.color || []).map((a) => ({ value: a._id, label: a.name })),
    [groupedAttrs]
  );
  const sizeOptions = useMemo(
    () => (groupedAttrs?.data?.size || []).map((a) => ({ value: a._id, label: a.name })),
    [groupedAttrs]
  );
  const settingTypeOptions = useMemo(
    () => (groupedAttrs?.data?.settingType || []).map((a) => ({ value: a._id, label: a.name })),
    [groupedAttrs]
  );

  const optionFromId = (opts, anyId) => {
    const idOnly =
      anyId && typeof anyId === "object"
        ? anyId._id || anyId.value || anyId.id || null
        : anyId || null;
    return idOnly ? opts.find((o) => String(o.value) === String(idOnly)) || null : null;
  };

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors, isSubmitting },
    setFocus,
  } = useForm({
    defaultValues: {
      unit: { value: "gram", label: "Gram" },
      deductable: { value: "yes", label: "Yes" },
      shape: null,
      color: null,
      size: null,
      settingType: null,
      stoneName: "",
      stnPopupWt: "",
      stnPopupRateByWt: "",
      stnPopupPcs: "",
      stnPopupRateByPcs: "",
      stnPopupAmount: "",
      stnPopupDescription: "",
    },
    mode: "onBlur",
  });

  // hydrate form when stone + options ready
  useEffect(() => {
    const s = stoneRes?.data;
    if (!s || attrLoading) return;

    reset({
      unit: UNIT_OPTS.find((u) => u.value === (s.unit || "gram")) || UNIT_OPTS[0],
      deductable: s.deductable ? DEDUCT_OPTS[0] : DEDUCT_OPTS[1],
      stoneName: s.stoneName || "",
      shape: optionFromId(shapeOptions, s.shape),
      color: optionFromId(colorOptions, s.color),
      size: optionFromId(sizeOptions, s.size),
      settingType: optionFromId(settingTypeOptions, s.settingType),
      stnPopupWt: s.stnPopupWt ?? "",
      stnPopupRateByWt: s.stnPopupRateByWt ?? "",
      stnPopupPcs: s.stnPopupPcs ?? "",
      stnPopupRateByPcs: s.stnPopupRateByPcs ?? "",
      stnPopupAmount: s.stnPopupAmount ?? "",
      stnPopupDescription: s.stnPopupDescription ?? "",
    });

    // focus first field after paint
    requestAnimationFrame(() => setFocus("stoneName"));
  }, [stoneRes, attrLoading, reset, shapeOptions, colorOptions, sizeOptions, settingTypeOptions, setFocus]);

  // toasts
  useEffect(() => {
    if (stoneErr) toast.error("Failed to load stone");
    if (attrError) toast.error("Failed to load attributes");
  }, [stoneErr, attrError]);

  // shortcuts: Ctrl+Backspace => back, Ctrl+S / Ctrl+Enter => save
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === "Backspace") {
        e.preventDefault();
        navigate(-1);
      }
      if ((e.ctrlKey && e.key.toLowerCase() === "s") || (e.ctrlKey && e.key === "Enter")) {
        e.preventDefault();
        handleSubmit(onSubmit)();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate, handleSubmit]);

  const unit = watch("unit");
  const loading = saving || loadingStone || isSubmitting;

  const onSubmit = async (form) => {
    const payload = {
      ...form,
      stoneName: form.stoneName?.trim(),
    };

    try {
      const res = await updateStone({ id, body: payload }).unwrap();
      toast.success("Stone updated");

      const updated = res?.data;
      if (updated) {
        const patched = (stonesData || []).map((x) =>
          String(x._id) === String(updated._id) ? updated : x
        );
        dispatch(setStoneList(patched));
      }

      // go back to list/screen
      navigate("/masters/stone-master-list");
    } catch (e) {
      const msg = e?.data?.message || e?.data?.msg || e?.error || e?.message || "Update failed";
      toast.error(msg);
      requestAnimationFrame(() => setFocus("stoneName"));
    }
  };

  return (
    <div className="px-4 py-4">
      {/* Compact Header (consistent look) */}
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-2 py-2 flex items-center gap-3">
          <Link
            to={"/masters/stone-master-list"}
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-200 hover:bg-gray-100 transition"
            title="Back to Stone List"
          >
            <FaArrowLeft className="text-[#6f5cc5] text-sm" />
          </Link>
          <div className="flex-1">
            <h1 className="text-xl md:text-2xl font-semibold text-[#4b3d91] leading-tight">
              {loadingStone ? "Loading…" : "Edit Stone"}
            </h1>
            <p className="text-xs text-gray-500">
              Tips: <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">S</kbd> or{" "}
              <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">Enter</kbd> to save,{" "}
              <kbd className="px-1 bg-gray-100 rounded border ml-2">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">Back Spc</kbd> go to list
            </p>
          </div>
        </div>
      </div>

      {/* Card — compact, border-only validation */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28 }}
        onAnimationComplete={() => requestAnimationFrame(() => setFocus("stoneName"))}
        className="max-w-5xl mx-auto bg-white shadow-md rounded-xl mt-4 border border-gray-200 relative"
      >
        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-20 rounded-xl">
            <div className="animate-spin h-7 w-7 border-2 border-[#9481ce] border-t-transparent rounded-full" />
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-3 gap-2 p-5">
          {/* Stone Name (required) */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Stone Name<span className="text-red-500">*</span>
            </label>
            <input
              {...register("stoneName", { required: true, minLength: 1, maxLength: 120 })}
              aria-invalid={!!errors.stoneName}
              placeholder="Enter stone name"
              autoComplete="off"
              className={`${baseInput} ${!!errors.stoneName ? errRing : okRing}`}
            />
          </div>

          {/* Unit (required) */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Unit<span className="text-red-500">*</span>
            </label>
            <Controller
              name="unit"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  {...field}
                  isClearable={false}
                  options={UNIT_OPTS}
                  placeholder="Select unit"
                  classNamePrefix="react-select"
                  styles={makeSelectStyles(!!errors.unit)}
                />
              )}
            />
          </div>

          {/* Deductable (required) */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Deductable<span className="text-red-500">*</span>
            </label>
            <Controller
              name="deductable"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  {...field}
                  isClearable={false}
                  options={DEDUCT_OPTS}
                  placeholder="Select"
                  classNamePrefix="react-select"
                  styles={makeSelectStyles(!!errors.deductable)}
                />
              )}
            />
          </div>

          {/* Optional attributes */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">Shape</label>
            <Controller
              name="shape"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  isClearable
                  isDisabled={attrLoading || attrError}
                  options={shapeOptions}
                  placeholder={attrLoading ? "Loading…" : "Select shape"}
                  classNamePrefix="react-select"
                  styles={makeSelectStyles(false)}
                />
              )}
            />
          </div>

          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">Color</label>
            <Controller
              name="color"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  isClearable
                  isDisabled={attrLoading || attrError}
                  options={colorOptions}
                  placeholder={attrLoading ? "Loading…" : "Select color"}
                  classNamePrefix="react-select"
                  styles={makeSelectStyles(false)}
                />
              )}
            />
          </div>

          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">Size</label>
            <Controller
              name="size"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  isClearable
                  isDisabled={attrLoading || attrError}
                  options={sizeOptions}
                  placeholder={attrLoading ? "Loading…" : "Select size"}
                  classNamePrefix="react-select"
                  styles={makeSelectStyles(false)}
                />
              )}
            />
          </div>

          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">Setting Type</label>
            <Controller
              name="settingType"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  isClearable
                  isDisabled={attrLoading || attrError}
                  options={settingTypeOptions}
                  placeholder={attrLoading ? "Loading…" : "Select setting type"}
                  classNamePrefix="react-select"
                  styles={makeSelectStyles(false)}
                />
              )}
            />
          </div>

          {/* By Weight (optional) */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Stone Weight {unit?.value === "gram" ? "(gm)" : "(ct)"}
            </label>
            <input
              {...register("stnPopupWt")}
              placeholder={`Stone Weight ${unit?.value === "gram" ? "(gm)" : "(ct)"}`}
              inputMode="decimal"
              autoComplete="off"
              disabled={!!watch("stnPopupPcs")}
              onKeyDown={(e) => handleDecimalKeyDown(e, watch("stnPopupWt") ?? "")}
              onPaste={handleDecimalPaste}
              className={`${baseInput} ${okRing} disabled:opacity-60`}
            />
          </div>

          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">Stone Rate / Weight</label>
            <input
              {...register("stnPopupRateByWt")}
              placeholder="Stone Rate/Wt"
              inputMode="decimal"
              autoComplete="off"
              disabled={!!watch("stnPopupRateByPcs")}
              onKeyDown={(e) => handleDecimalKeyDown(e, watch("stnPopupRateByWt") ?? "")}
              onPaste={handleDecimalPaste}
              className={`${baseInput} ${okRing} disabled:opacity-60`}
            />
          </div>

          {/* By Pieces (optional) */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">Stone Pieces</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              {...register("stnPopupPcs")}
              placeholder="Stone Pieces"
              onBeforeInput={handleIntegerBeforeInput}
              onKeyDown={handleIntegerKeyDown}
              onPaste={handleIntegerPaste}
              disabled={!!watch("stnPopupWt")}
              className={`${baseInput} ${okRing} disabled:opacity-60`}
            />
          </div>

          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">Stone Rate / Pieces</label>
            <input
              {...register("stnPopupRateByPcs")}
              placeholder="Stone Rate/Pcs"
              inputMode="decimal"
              autoComplete="off"
              disabled={!!watch("stnPopupRateByWt")}
              onKeyDown={(e) => handleDecimalKeyDown(e, watch("stnPopupRateByPcs") ?? "")}
              onPaste={handleDecimalPaste}
              className={`${baseInput} ${okRing} disabled:opacity-60`}
            />
          </div>

          {/* Amount & Desc (optional) */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">Stone Amount</label>
            <input
              {...register("stnPopupAmount")}
              placeholder="Enter stone amount"
              autoComplete="off"
              className={`${baseInput} ${okRing}`}
            />
          </div>

          <div className="md:col-span-3">
            <label className="block text-[13px] font-medium text-gray-700 mb-1">Stone Description</label>
            <input
              {...register("stnPopupDescription")}
              placeholder="Enter stone description"
              autoComplete="off"
              className={`${baseInput} ${okRing}`}
            />
          </div>

          {/* Actions */}
          <div className="md:col-span-3 flex flex-col sm:flex-row gap-2 pt-1">
            <motion.button
              whileHover={{ scale: loading ? 1 : 1.01 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              type="submit"
              disabled={loading}
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium shadow-sm bg-[#9481ce] hover:bg-[#7d6ec0] text-white disabled:opacity-60"
            >
              {saving ? "Updating…" : "Save Changes"}
            </motion.button>

            <Link
              to="/masters/stone-master-list"
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
              title="Cancel and go back"
            >
              Cancel
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default EditStone;
